import React, { useEffect, useState, useCallback } from "react";
import { Snackbar } from "@mui/material";
import Header from "src/components/Header";
import SearchForm from "src/components/SearchForm";
import NewClientModal from "src/components/NewClientModal";
import DataTable, { Column } from "src/components/Table";
import { getAllCustomersAndEquipments, getCustomer, deleteCustomer } from "src/services/customers";
import { SnackbarContentContainer, StyledErrorContainer } from "./styles";
import ErrorMessage from "src/components/ErrorMessage";
import TableSection from "src/components/TableSecion";
import { DashboardMode, DocumentRow, EquipmentRow } from "./interfaces";
import Loading from "src/components/Loading";
import Pagination from "src/components/Pagination";
import { me } from "src/services/me";
import { formatDate } from "src/utils/formatters";
import { getDocuments, deleteDocument } from "src/services/documents";
import { deleteEquipment } from "src/services/equipments";

export const Dashboard: React.FC = () => {
  const [mode, setMode] = useState<DashboardMode>("certificates");
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const [allDocuments, setAllDocuments] = useState<any[]>([]);
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [openModal, setOpenModal] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [pages, setPages] = useState({ certificates: 1, documents: 1 });

  const rowsPerPage = 15;
  const currentPage = pages[mode];

  const storeMeData = (role: string) => {
    const expiration = Date.now() + 10 * 24 * 60 * 60 * 1000;
    localStorage.setItem("meData", JSON.stringify({ role, expiration }));
    setUserRole(role);
  };

  const checkUserAuth = useCallback(async () => {
    const stored = localStorage.getItem("meData");

    if (stored) {
      const { role, expiration } = JSON.parse(stored);
      if (Date.now() < expiration) {
        setUserRole(role);
        return;
      }
    }

    try {
      const response = await me();
      const role = response?.data?.data?.role;
      if (role) storeMeData(role);
    } catch {
      setToast({
        message: "Sessão expirada ou erro ao validar usuário.",
        type: "error",
      });
    }
  }, []);

  const handleModeChange = (newMode: DashboardMode) => {
    if (newMode === "documents" && userRole !== "admin") {
      setToast({
        message:
          "Acesso restrito: apenas administradores podem acessar a aba de Documentos.",
        type: "error",
      });
      return;
    }
    setTableRows([]);
    setMode(newMode);
  };

  const isDateValidOrFuture = (dateString: string) => {
    if (!dateString) return false;
    const [day, month, year] = dateString.split("/");
    const formattedDate = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    formattedDate.setHours(0, 0, 0, 0);
    return formattedDate >= today;
  };

  const mapCustomersToCertificates = (customers: any[]): EquipmentRow[] => {
    return customers
      .flatMap((customer: any) =>
        (customer.equipment || []).map((eq: any) => ({
          customerId: customer.id,
          equipmentId: eq.id,
          document: customer.document,
          cliente: customer.name,
          equipamento: eq.name,
          numSerie: eq.serial_number,
          marca: eq.brand,
          tecnico: eq.employee_name,
          vencimentoOriginal: eq.expiration_date,
          vencimento: formatDate(eq.expiration_date),
        })),
      )
      .sort(
        (a, b) =>
          new Date(a.vencimentoOriginal).getTime() -
          new Date(b.vencimentoOriginal).getTime(),
      );
  };

  const mapDocumentsToRows = (documents: any[]): DocumentRow[] => {
    return (documents || []).map((doc: any) => ({
      customerId: doc.customer_id || doc.customers?.[0]?.id,
      documentId: doc.id,
      cliente: doc.customers?.[0]?.name || "-",
      documentName: doc.name || "-",
      tecnico: doc.employees?.[0]?.name || "-",
      validade: formatDate(doc.expiration_date),
      status: doc.status || "-",
    }));
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [customersResponse, documentsResponse] = await Promise.all([
        getAllCustomersAndEquipments(),
        getDocuments(),
      ]);

      const customers = customersResponse?.data?.data || [];
      const documents = documentsResponse?.data || [];

      setAllCustomers(customers);
      setAllDocuments(documents);

      if (mode === "certificates") {
        setTableRows(mapCustomersToCertificates(customers));
      } else if (mode === "documents") {
        setTableRows(mapDocumentsToRows(documents));
      }
    } catch {
      setToast({ message: "Erro ao carregar dados.", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [mode]);

  const handleView = async (row: any) => {
    try {
      setLoading(true);
      const response = await getCustomer(row.customerId);
      setSelectedCustomer(response.data.data);
      setOpenModal(true);
    } catch {
      setToast({ message: "Erro ao carregar cliente.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row: any) => {
    const isEquipment = mode === "certificates";
    const confirmMessage = isEquipment
      ? "Tem certeza que deseja excluir este certificado?"
      : "Tem certeza que deseja excluir este documento?";
    if (!window.confirm(confirmMessage)) return;
    try {
      setLoading(true);
      const response = isEquipment
        ? await deleteEquipment(row.equipmentId)
        : await deleteDocument(row.documentId);
      if (response?.status === 204 || response?.status === 200) {
        const successMessage = isEquipment
          ? "Certificado excluído com sucesso!"
          : "Documento excluído com sucesso!";
        setToast({ message: successMessage, type: "success" });
        loadData();
      }
    } catch (error: any) {
      console.error("Erro ao deletar:", error);

      if (error?.response?.status === 403 && isEquipment) {
        const confirmDeleteCustomer = window.confirm(
          "ATENÇÃO! Este cliente não possui mais certificados. Deseja excluir o cadastro do cliente?",
        );
        if (confirmDeleteCustomer) {
          try {
            await deleteCustomer(row.customerId);
            setToast({
              message: "Cliente excluído com sucesso!",
              type: "success",
            });
            loadData();
          } catch (deleteError: any) {
            console.error("Erro ao excluir cliente:", deleteError);
            setToast({
              message: "Erro ao excluir cliente. Tente novamente.",
              type: "error",
            });
          }
        }
      } else {
        const errorMessage = isEquipment
          ? "Erro ao excluir certificado. Tente novamente."
          : "Erro ao excluir documento. Tente novamente.";
        setToast({ message: errorMessage, type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = useCallback((filtered: any[]) => {
    if (mode === "certificates") {
      setTableRows(mapCustomersToCertificates(filtered));
    } else {
      setTableRows(mapDocumentsToRows(filtered));
    }

    setPages((prev) => ({ ...prev, [mode]: 1 }));
  }, [mode]);

  useEffect(() => {
    checkUserAuth();
  }, [checkUserAuth]);

  useEffect(() => {
    loadData();
  }, [mode, loadData]);

  const totalPages = Math.ceil(tableRows.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedRows = tableRows.slice(startIndex, startIndex + rowsPerPage);

  const setCurrentPage = (page: number) => {
    setPages((prev) => ({ ...prev, [mode]: page }));
  };

  const certificateColumns: Column<EquipmentRow>[] = [
    { header: "Cliente", field: "cliente" },
    { header: "CPF/CNPJ", field: "document" },
    { header: "Equipamento", field: "equipamento" },
    { header: "Nº Série", field: "numSerie" },
    { header: "Marca", field: "marca" },
    { header: "Técnico", field: "tecnico" },
    {
      header: "Vencimento",
      render: (row) => {
        const isValid = isDateValidOrFuture(row.vencimento);
        return (
          <span style={{ color: isValid ? "green" : "red", fontWeight: 600 }}>
            {row.vencimento}
          </span>
        );
      },
    },
  ];

  const documentColumns: Column<DocumentRow>[] = [
    { header: "Cliente", field: "cliente" },
    { header: "Nome do Documento", field: "documentName" },
    { header: "Funcionário", field: "tecnico" },
    {
      header: "Vencimento",
      render: (row) => {
        const isValid = isDateValidOrFuture(row.validade);
        return (
          <span style={{ color: isValid ? "green" : "red", fontWeight: 600 }}>
            {row.validade}
          </span>
        );
      },
    },
    {
      header: "Status",
      render: (row) => {
        return <span style={{ fontWeight: 600 }}>{row.status === "Ativo" ? "A vencer" : row.status}</span>;
      },
    },
  ];

  return (
    <div>
      {loading && <Loading />}

      <Header />

      <SearchForm
        mode={mode}
        customers={allCustomers}
        documents={allDocuments}
        onFilter={handleFilter}
        isAdmin={userRole === "admin"}
      />

      <TableSection
        mode={mode}
        onChangeMode={handleModeChange}
        isAdmin={userRole === "admin"}
        certificateCount={allCustomers.flatMap((c) => c.equipment || []).length}
        documentCount={allDocuments.length}
      >
        {paginatedRows.length > 0 ? (
          <DataTable
            columns={
              mode === "certificates"
                ? certificateColumns
                : (documentColumns as any)
            }
            rows={paginatedRows}
            minWidthTable={900}
            onView={handleView}
            onDelete={userRole === "admin" ? handleDelete : undefined}
          />
        ) : (
          !loading && (
            <StyledErrorContainer>
              <ErrorMessage
                message="Nenhum registro encontrado."
                type="warning"
              />
            </StyledErrorContainer>
          )
        )}

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
          />
        )}
      </TableSection>

      {openModal && userRole && (
        <NewClientModal
          key={userRole}
          open={openModal}
          customer={selectedCustomer}
          tabMode={mode === "certificates" ? 0 : 1}
          isAdmin={userRole === "admin"}
          handleClose={() => {
            setOpenModal(false);
            setTimeout(() => loadData(), 0);
          }}
        />
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContentContainer
          message={toast?.message}
          sx={{
            backgroundColor: "#fff",
            color: toast?.type === "success" ? "#4caf50" : "#f44336",
            fontWeight: 700,
          }}
        />
      </Snackbar>
    </div>
  );
};
